import React, { FunctionComponent } from 'react';
import { css } from '@emotion/react';
import Button from '.';
import { Spinner } from '$/images/Icons';
import { ButtonT, useThemeContext } from '../ThemeContext';

export interface SubmitingDetails {
  text: string;
  progress?: number;
  progressBg?: string;
  progressFg?: string;
}

export interface SubmitButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  submitting: boolean | SubmitingDetails;
  neverDisable?: boolean;
  dirty?: boolean;
  dirtyText?: string;
  cleanText?: string;
  hideOnClean?: boolean;
  formId?: string;
  inverted?: boolean;
  button?: React.FC;
  theme?: ButtonT;
  icon?: React.FC<{ className?: string }>;
}

const Submit: FunctionComponent<SubmitButtonProps> = React.memo(
  ({
    cleanText,
    dirtyText,
    hideOnClean,
    submitting,
    dirty,
    disabled,
    formId,
    icon: Icon,
    button = Button.rounded,
    type = 'submit',
    neverDisable,
    ...props
  }) => {
    const cleanTxt = cleanText || props.children || 'Close';
    const dirtyTxt = dirtyText || props.children || 'Save';
    const etheme = useThemeContext();

    let holdSpace = dirtyTxt;
    if (((cleanTxt as string)?.length ?? 0) > ((dirtyTxt as string)?.length ?? 0)) {
      holdSpace = cleanTxt;
    }

    const Btn = button;

    if (!props.theme) {
      props.theme = etheme.controls.button.branded;
    }

    const submitDetails = typeof submitting === 'object' ? submitting : undefined;

    const [clipPath, setClipPath] = React.useState('polygon(0 0, 0 0, 0 100%, 0 100%)'); // State for clip-path
    const progress = submitDetails?.progress ?? 0; // Get the progress from the submitDetails object

    const handleRef = (element: HTMLDivElement) => {
      if (element) {
        // Get bounding rects for the element and its container
        const elementRect = element.getBoundingClientRect();
        const parentRect = element.parentElement?.getBoundingClientRect();

        if (parentRect) {
          // Calculate total progress in pixels relative to the parent container
          const totalProgressPixels = (progress / 100) * parentRect.width;

          // Adjust for the offset of the element within the parent
          const adjustedPixels = totalProgressPixels - (elementRect.left - parentRect.left);

          // Update the clip-path in pixels
          setClipPath(`polygon(
        0px 0px,
        ${adjustedPixels}px 0px,
        ${adjustedPixels}px 100%,
        0px 100%
      )`);
        }
      }
    };

    return (
      <Btn
        css={[
          css`
            min-width: 8rem;
            white-space: no-wrap;
            padding-right: 1rem;
            padding-left: 1rem;
            transition: background-color 0.2s;
            visibility: ${hideOnClean && !dirty && !submitting ? 'hidden' : ''};
            &:disabled {
              cursor: auto;
            }
          `,
          !submitDetails
            ? undefined
            : css`
                background: linear-gradient(
                  to right,
                  ${submitDetails.progressBg ?? '#fff'} 0%,
                  ${submitDetails.progressBg ?? '#fff'} ${Number(submitDetails.progress)}%,
                  transparent ${Number(submitDetails.progress)}%,
                  transparent 100%
                ) !important;
                background-size: 100% 100% !important;
                background-clip: border-box !important;
                transition: background-size 0.5s ease !important;
                box-sizing: border-box;
              `,
        ]}
        {...props}
        className={`button duration-200 transition-color flex-col relative ${props.className}`}
        type={type}
        form={formId}
        disabled={!neverDisable && (disabled || Boolean(submitting))}
      >
        <>
          {
            <div
              className={`inline-flex m-auto ${!submitting && !dirty ? '' : 'hidden'}`}
              css={css`
                position: relative;
                height: 0;
              `}
            >
              {Icon && <Icon className='h-1e mr-1/2e' />}
              {cleanTxt}
            </div>
          }
          {
            <div
              className={`inline-flex m-auto ${!submitting && dirty ? '' : 'hidden'}`}
              css={css`
                position: relative;
                height: 0;
              `}
            >
              {Icon && <Icon className='h-1e mr-1/2e' />}
              {dirtyTxt}
            </div>
          }
          {submitting && (
            <>
              <div className='inline-flex absolute'>
                <Spinner
                  alt='spinning'
                  className={`spinner m-auto`}
                  css={css`
                    height: 1.25em;
                  `}
                />
                {submitDetails?.text && (
                  <span className='ml-1/2e' css={css``}>
                    {submitDetails?.text}
                  </span>
                )}
              </div>
              <div
                ref={handleRef}
                className='progress-text inline-flex absolute'
                css={css`
                  color: ${submitDetails?.progressFg ?? '#000'};
                  clip-path: ${clipPath};
                  box-sizing: border-box;
                `}
              >
                <Spinner
                  alt='spinning'
                  className={`spinner m-auto`}
                  css={css`
                    height: 1.25em;
                  `}
                />
                {submitDetails?.text && (
                  <span className='ml-1/2e' css={css``}>
                    {submitDetails?.text}
                  </span>
                )}
              </div>
            </>
          )}
          <div className='inline-flex invisible'>
            {Icon && <Icon className='h-1e mr-1/2e' />}
            {holdSpace}
          </div>
        </>
      </Btn>
    );
  }
);

export default Submit;
